import {
  type AdminAccountCatalog,
  type AdminAccountPayment,
  type AdminAccountSetupIntent,
  type AdminAccountUpgradeAction,
  type AdminAccountUpgradeRequest,
  type AdminAddress,
  type AdminAddressCheck,
  type AdminAlertCatalog,
  type AdminAuthActionResponse,
  type AdminAuthTokenStatus,
  type AdminBotProfile,
  type AdminBotProfileApply,
  type AdminBotProfileRequest,
  type AdminBrandingAsset,
  type AdminBrandingSettings,
  type AdminCoupon,
  type AdminCouponCatalog,
  type AdminCreateCouponRequest,
  type AdminCreateDiscordRoleRequest,
  type AdminCreateFreeProductRequest,
  type AdminCreateMemberRequest,
  type AdminCreateProductRequest,
  type AdminCreateRewardRequest,
  type AdminCreateTeamMemberRequest,
  type AdminCustomerDetail,
  type AdminCustomerFacets,
  type AdminCustomerList,
  type AdminDashboardRange,
  type AdminDashboardSummary,
  type AdminDeveloperKeyIssued,
  type AdminDeveloperSettings,
  type AdminDiscordConnection,
  type AdminDiscordMember,
  type AdminDomainCatalog,
  type AdminForgotPasswordRequest,
  type AdminFreeProduct,
  type AdminLegalLinkCheck,
  type AdminLoginRequest,
  type AdminLoginResponse,
  type AdminMcpConnectionList,
  type AdminMcpOauthRequest,
  type AdminMcpTeam,
  type AdminMcpTokenCreated,
  type AdminMcpTokenList,
  type AdminMember,
  type AdminMemberCatalog,
  type AdminMemberDetail,
  type AdminMemberTracking,
  type AdminMemberTrackingRequest,
  type AdminOnboardingAccountRequest,
  type AdminOnboardingBrandingRequest,
  type AdminOnboardingCatalog,
  type AdminOnboardingComplete,
  type AdminOnboardingConnect,
  type AdminOnboardingLink,
  type AdminPasswordActionRequest,
  type AdminPaymentGatewayCatalog,
  type AdminPaymentGatewayConnect,
  type AdminPoll,
  type AdminPollChannels,
  type AdminPollCreateRequest,
  type AdminPollList,
  type AdminProduct,
  type AdminProductCatalog,
  type AdminProductGuild,
  type AdminRegistrationAuthorize,
  type AdminRegistrationContext,
  type AdminRegistrationCoupon,
  type AdminRegistrationEmailCode,
  type AdminRegistrationRequest,
  type AdminRegistrationResponse,
  type AdminRegistrationSetupIntent,
  type AdminReward,
  type AdminRewardCatalog,
  type AdminSavedChannels,
  type AdminSavedChannelsRequest,
  type AdminSavedPostDetail,
  type AdminSavedPostList,
  type AdminSessionResponse,
  type AdminSetup,
  type AdminTeamCatalog,
  type AdminTrackingPixels,
  type AdminUpdateBrandingRequest,
  type AdminUpdateDiscordBotRequest,
  type AdminUpdateDiscordEmbedsRequest,
  type AdminUpdateDiscordWebhooksRequest,
  type AdminUpdateMemberRequest,
  type AdminUpdatePaymentGatewayRequest,
  type AdminUpdateProductRequest,
  type AdminUpdateTrackingPixelsRequest,
  type AdminUpdateWelcomeEmailRequest,
  adminAccountCatalogSchema,
  adminAccountPaymentSchema,
  adminAccountSetupIntentSchema,
  adminAccountUpgradeActionSchema,
  adminAddressCheckSchema,
  adminAddressSchema,
  adminAlertCatalogSchema,
  adminAuthActionResponseSchema,
  adminAuthTokenStatusSchema,
  adminBotProfileApplySchema,
  adminBotProfileSchema,
  adminBrandingAssetSchema,
  adminBrandingSettingsSchema,
  adminCouponCatalogSchema,
  adminCouponSchema,
  adminCustomerDetailSchema,
  adminCustomerFacetsSchema,
  adminCustomerListSchema,
  adminDashboardQuerySchema,
  adminDashboardSummarySchema,
  adminDeveloperKeyIssuedSchema,
  adminDeveloperSettingsSchema,
  adminDiscordConnectionSchema,
  adminDiscordMemberSchema,
  adminDiscordRoleSchema,
  adminDomainCatalogSchema,
  adminFreeProductSchema,
  adminLegalLinkCheckSchema,
  adminLoginResponseSchema,
  adminMcpConnectionListSchema,
  adminMcpOauthDecisionSchema,
  adminMcpOauthRequestSchema,
  adminMcpTeamSchema,
  adminMcpTokenCreatedSchema,
  adminMcpTokenListSchema,
  adminMemberCatalogSchema,
  adminMemberDetailSchema,
  adminMemberSchema,
  adminMemberTrackingSchema,
  adminOnboardingCatalogSchema,
  adminOnboardingCompleteSchema,
  adminOnboardingConnectSchema,
  adminOnboardingLinkSchema,
  adminPaymentGatewayCatalogSchema,
  adminPaymentGatewayConnectSchema,
  adminPollChannelsSchema,
  adminPollListSchema,
  adminPollSchema,
  adminProductCatalogSchema,
  adminProductSchema,
  adminRegistrationAuthorizeSchema,
  adminRegistrationContextSchema,
  adminRegistrationCouponSchema,
  adminRegistrationEmailCodeSchema,
  adminRegistrationResponseSchema,
  adminRegistrationSetupIntentSchema,
  adminRewardCatalogSchema,
  adminRewardSchema,
  adminSavedChannelsSchema,
  adminSavedPostDetailSchema,
  adminSavedPostListSchema,
  adminSessionResponseSchema,
  adminSetupSchema,
  adminTeamCatalogSchema,
  adminTrackingPixelsSchema,
  objectIdSchema,
} from "@ternary/contracts"
import { type CustomerFilters, customerQuery } from "./customer-filters.js"

// The error body's message, by the one rule every admin call uses, and its `reason` when a string.
async function errorBody(response: Response, fallback: string) {
  try {
    const body = (await response.json()) as { error?: string; reason?: unknown }
    return {
      message: body.error ?? fallback,
      reason: typeof body.reason === "string" ? body.reason : null,
    }
  } catch {
    return { message: fallback, reason: null }
  }
}

async function errorMessage(response: Response, fallback: string) {
  return (await errorBody(response, fallback)).message
}

function responseRequestId(response: Response) {
  return response.headers?.get("x-request-id") ?? null
}

export class AdminApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly requestId: string | null,
    /** The error body's `reason`, such as the onboarding catalog's 410 `expired`. */
    readonly reason: string | null = null,
  ) {
    super(message)
    this.name = "AdminApiError"
  }
}

export interface AdminRequestContext {
  signal?: AbortSignal
}

interface ResponseSchema<T> {
  parse(value: unknown): T
}

async function getAdminJson<T>(
  path: string,
  schema: ResponseSchema<T>,
  fallback: string,
  context: AdminRequestContext = {},
): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    signal: context.signal ?? null,
  })
  if (!response.ok) {
    const { message, reason } = await errorBody(response, fallback)
    throw new AdminApiError(message, response.status, responseRequestId(response), reason)
  }
  return schema.parse(await response.json())
}

export class AdminRenewalRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

export async function loadAdminRegistrationAuthorize(
  context: AdminRequestContext = {},
): Promise<AdminRegistrationAuthorize> {
  return getAdminJson(
    "/api/v1/admin/registration/authorize",
    adminRegistrationAuthorizeSchema,
    "Could not start Discord authorization.",
    context,
  )
}

export async function loadAdminRegistrationContext(
  input: {
    code: string
    guildId: string
    state: string
  },
  context: AdminRequestContext = {},
): Promise<AdminRegistrationContext> {
  const query = new URLSearchParams({
    code: input.code,
    guild_id: input.guildId,
    state: input.state,
  })
  return getAdminJson(
    `/api/v1/admin/registration/context?${query}`,
    adminRegistrationContextSchema,
    "Discord authorization failed.",
    context,
  )
}

export async function loadAdminRegistrationCoupon(code: string): Promise<AdminRegistrationCoupon> {
  const response = await fetch(`/api/v1/admin/registration/coupons/${encodeURIComponent(code)}`, {
    headers: { Accept: "application/json" },
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Coupon not found."))
  }
  return adminRegistrationCouponSchema.parse(await response.json())
}

export async function createAdminRegistrationSetupIntent(
  contextToken: string,
  context: AdminRequestContext = {},
): Promise<AdminRegistrationSetupIntent> {
  const response = await fetch("/api/v1/admin/registration/setup-intent", {
    body: JSON.stringify({ contextToken }),
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
    signal: context.signal ?? null,
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not prepare secure card entry."))
  }
  return adminRegistrationSetupIntentSchema.parse(await response.json())
}

/** Sends a one-time code to `email`, for a registration whose Discord account gave none (G-8). */
export async function requestAdminRegistrationEmailCode(
  contextToken: string,
  email: string,
): Promise<AdminRegistrationEmailCode> {
  const response = await fetch("/api/v1/admin/registration/email", {
    body: JSON.stringify({ contextToken, email }),
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "The code could not be sent. Try again."))
  }
  return adminRegistrationEmailCodeSchema.parse(await response.json())
}

/** Confirms the code; the answer is the registration context with the confirmed email. */
export async function verifyAdminRegistrationEmail(input: {
  code: string
  codeToken: string
  contextToken: string
}): Promise<AdminRegistrationContext> {
  const response = await fetch("/api/v1/admin/registration/email/verify", {
    body: JSON.stringify(input),
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "The code could not be checked. Try again."))
  }
  return adminRegistrationContextSchema.parse(await response.json())
}

export async function registerAdmin(
  registration: AdminRegistrationRequest,
): Promise<AdminRegistrationResponse> {
  const response = await fetch("/api/v1/admin/registration", {
    body: JSON.stringify(registration),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Registration failed."))
  }
  return adminRegistrationResponseSchema.parse(await response.json())
}

function adminOnboardingPath(token: string, action?: string) {
  const path = `/api/v1/admin/onboarding/${encodeURIComponent(token)}`
  return action ? `${path}/${action}` : path
}

export async function loadAdminOnboarding(
  token: string,
  context: AdminRequestContext = {},
): Promise<AdminOnboardingCatalog> {
  return getAdminJson(
    adminOnboardingPath(token),
    adminOnboardingCatalogSchema,
    "Could not load onboarding.",
    context,
  )
}

// Discord has no step here: onboarding adds the bot through the install at the catalog's
// `discord.authorizationUrl`, and the old server-ID POST answers 409 (fix d).
async function updateAdminOnboarding(
  token: string,
  action: "account" | "branding" | "pixels",
  input:
    | AdminOnboardingAccountRequest
    | AdminOnboardingBrandingRequest
    | AdminUpdateTrackingPixelsRequest,
): Promise<AdminOnboardingCatalog> {
  const response = await fetch(adminOnboardingPath(token, action), {
    body: JSON.stringify(input),
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    // With the status, so the page can tell a spent link (410) from a refused step.
    throw new AdminApiError(
      await errorMessage(response, "Could not update onboarding."),
      response.status,
      responseRequestId(response),
    )
  }
  return adminOnboardingCatalogSchema.parse(await response.json())
}

/** The Tracking step (§3.7): all three IDs, which replace the saved ones. All empty skips it. */
export function updateAdminOnboardingPixels(
  token: string,
  pixels: AdminUpdateTrackingPixelsRequest,
): Promise<AdminOnboardingCatalog> {
  return updateAdminOnboarding(token, "pixels", pixels)
}

export function updateAdminOnboardingBranding(
  token: string,
  input: AdminOnboardingBrandingRequest,
) {
  return updateAdminOnboarding(token, "branding", input)
}

export function completeAdminOnboardingAccount(
  token: string,
  input: AdminOnboardingAccountRequest,
) {
  return updateAdminOnboarding(token, "account", input)
}

export async function createAdminOnboardingSetupIntent(
  token: string,
  context: AdminRequestContext = {},
): Promise<AdminRegistrationSetupIntent> {
  const response = await fetch(adminOnboardingPath(token, "setup-intent"), {
    headers: { Accept: "application/json" },
    method: "POST",
    signal: context.signal ?? null,
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "Could not prepare secure card entry."),
      response.status,
      responseRequestId(response),
    )
  }
  return adminRegistrationSetupIntentSchema.parse(await response.json())
}

export async function connectAdminOnboarding(token: string): Promise<AdminOnboardingConnect> {
  const response = await fetch(adminOnboardingPath(token, "connect"), {
    headers: { Accept: "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "Could not start Stripe Connect."),
      response.status,
      responseRequestId(response),
    )
  }
  return adminOnboardingConnectSchema.parse(await response.json())
}

export async function verifyAdminOnboarding(token: string): Promise<AdminOnboardingComplete> {
  const response = await fetch(adminOnboardingPath(token, "verify"), {
    headers: { Accept: "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "Could not complete onboarding."),
      response.status,
      responseRequestId(response),
    )
  }
  return adminOnboardingCompleteSchema.parse(await response.json())
}

export async function loadAdminMembers(
  context: AdminRequestContext = {},
): Promise<AdminMemberCatalog> {
  return getAdminJson(
    "/api/v1/admin/users",
    adminMemberCatalogSchema,
    "Could not load users.",
    context,
  )
}

export async function loadAdminMember(
  id: string,
  context: AdminRequestContext = {},
): Promise<AdminMemberDetail> {
  return getAdminJson(
    `/api/v1/admin/users/${encodeURIComponent(id)}`,
    adminMemberDetailSchema,
    "Could not load the user profile.",
    context,
  )
}

export async function lookupAdminDiscordMember(discordId: string): Promise<AdminDiscordMember> {
  const response = await fetch(`/api/v1/admin/users/discord/${encodeURIComponent(discordId)}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Discord member not found."))
  }
  return adminDiscordMemberSchema.parse(await response.json())
}

export async function createAdminMember(member: AdminCreateMemberRequest): Promise<AdminMember> {
  const response = await fetch("/api/v1/admin/users", {
    body: JSON.stringify(member),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    // With its status, so Add user can tell a license key already in use (409) from the rest.
    throw new AdminApiError(
      await errorMessage(response, "Could not create user."),
      response.status,
      responseRequestId(response),
    )
  }
  return adminMemberSchema.parse(await response.json())
}

export async function updateAdminMember(
  id: string,
  member: AdminUpdateMemberRequest,
): Promise<AdminMember> {
  const response = await fetch(`/api/v1/admin/users/${encodeURIComponent(id)}`, {
    body: JSON.stringify(member),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "PUT",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not update user."))
  }
  return adminMemberSchema.parse(await response.json())
}

export async function deleteAdminMembers(ids: string[]) {
  const response = await fetch("/api/v1/admin/users", {
    body: JSON.stringify({ ids }),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "DELETE",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not delete users."))
  }
}

export async function loadAdminProducts(
  context: AdminRequestContext = {},
): Promise<AdminProductCatalog> {
  return getAdminJson(
    "/api/v1/admin/products",
    adminProductCatalogSchema,
    "Could not load products.",
    context,
  )
}

/**
 * Sends a product: a free one (`pricing: { type: "free" }`) or a paid one in the flat shape, on the
 * same endpoint. A refusal is an AdminApiError, whose status tells a taken signup link (409) apart.
 */
async function sendAdminProduct(
  path: string,
  method: "POST" | "PUT",
  product: AdminCreateFreeProductRequest | AdminCreateProductRequest | AdminUpdateProductRequest,
  fallback: string,
): Promise<AdminFreeProduct | AdminProduct> {
  const response = await fetch(path, {
    body: JSON.stringify(product),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method,
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, fallback),
      response.status,
      responseRequestId(response),
    )
  }
  const body: unknown = await response.json()
  return "pricing" in product ? adminFreeProductSchema.parse(body) : adminProductSchema.parse(body)
}

export function createAdminProduct(
  product: AdminCreateFreeProductRequest | AdminCreateProductRequest,
): Promise<AdminFreeProduct | AdminProduct> {
  return sendAdminProduct("/api/v1/admin/products", "POST", product, "Could not create product.")
}

/** PUT replaces the whole record, so send every field. A product keeps its pricing type. */
export function updateAdminProduct(
  id: string,
  product: AdminCreateFreeProductRequest | AdminUpdateProductRequest,
): Promise<AdminFreeProduct | AdminProduct> {
  return sendAdminProduct(
    `/api/v1/admin/products/${encodeURIComponent(id)}`,
    "PUT",
    product,
    "Could not update product.",
  )
}

/** Deletes paid products and archives free ones (204). */
export async function deleteAdminProducts(ids: string[]) {
  const response = await fetch("/api/v1/admin/products", {
    body: JSON.stringify({ ids }),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "DELETE",
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "Could not delete products."),
      response.status,
      responseRequestId(response),
    )
  }
}

export async function loadAdminSession(
  context: AdminRequestContext = {},
): Promise<AdminSessionResponse> {
  const response = await fetch("/api/v1/admin/session", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    signal: context.signal ?? null,
  })

  if (response.status === 401) {
    return { authenticated: false }
  }
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "Could not check the admin session."),
      response.status,
      responseRequestId(response),
    )
  }

  return adminSessionResponseSchema.parse(await response.json())
}

export async function loginAdmin(credentials: AdminLoginRequest): Promise<AdminLoginResponse> {
  const response = await fetch("/api/organization/login", {
    body: JSON.stringify(credentials),
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "POST",
  })

  if (!response.ok) {
    throw new Error(await errorMessage(response, "Sign in failed."))
  }

  return adminLoginResponseSchema.parse(await response.json())
}

export async function requestAdminPasswordReset(
  input: AdminForgotPasswordRequest,
): Promise<AdminAuthActionResponse> {
  const response = await fetch("/api/v1/admin/auth/forgot", {
    body: JSON.stringify(input),
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not request a password reset."))
  }
  return adminAuthActionResponseSchema.parse(await response.json())
}

export async function loadAdminCredentialToken(
  purpose: "create" | "reset",
  token: string,
  context: AdminRequestContext = {},
): Promise<AdminAuthTokenStatus> {
  return getAdminJson(
    `/api/v1/admin/auth/${purpose}/${encodeURIComponent(token)}`,
    adminAuthTokenStatusSchema,
    "Could not verify this link.",
    context,
  )
}

export async function completeAdminCredential(
  purpose: "create" | "reset",
  token: string,
  input: AdminPasswordActionRequest,
): Promise<AdminAuthActionResponse> {
  const response = await fetch(`/api/v1/admin/auth/${purpose}/${encodeURIComponent(token)}`, {
    body: JSON.stringify(input),
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not update your password."))
  }
  return adminAuthActionResponseSchema.parse(await response.json())
}

export async function logoutAdmin() {
  const response = await fetch("/api/v1/admin/logout", {
    credentials: "same-origin",
    method: "POST",
  })
  if (!response.ok) {
    throw new Error("Sign out failed.")
  }
}

export async function loadAdminPaymentGateway(
  context: AdminRequestContext = {},
): Promise<AdminPaymentGatewayCatalog> {
  return getAdminJson(
    "/api/v1/admin/payment-gateway",
    adminPaymentGatewayCatalogSchema,
    "Could not load payment gateway settings.",
    context,
  )
}

export async function updateAdminPaymentGateway(
  settings: AdminUpdatePaymentGatewayRequest,
): Promise<AdminPaymentGatewayCatalog> {
  const response = await fetch("/api/v1/admin/payment-gateway", {
    body: JSON.stringify(settings),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "PUT",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not update payment gateway settings."))
  }
  return adminPaymentGatewayCatalogSchema.parse(await response.json())
}

export async function connectAdminPaymentGateway(): Promise<AdminPaymentGatewayConnect> {
  const response = await fetch("/api/v1/admin/payment-gateway/connect", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not start Stripe Connect."))
  }
  return adminPaymentGatewayConnectSchema.parse(await response.json())
}

export async function loadAdminAlerts(
  context: AdminRequestContext = {},
): Promise<AdminAlertCatalog> {
  return getAdminJson(
    "/api/v1/admin/alerts",
    adminAlertCatalogSchema,
    "Could not load admin alerts.",
    context,
  )
}

export async function loadAdminCoupons(
  context: AdminRequestContext = {},
): Promise<AdminCouponCatalog> {
  return getAdminJson(
    "/api/v1/admin/coupons",
    adminCouponCatalogSchema,
    "Could not load coupons.",
    context,
  )
}

export async function createAdminCoupon(coupon: AdminCreateCouponRequest): Promise<AdminCoupon> {
  const response = await fetch("/api/v1/admin/coupons", {
    body: JSON.stringify(coupon),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not create coupon."))
  }
  return adminCouponSchema.parse(await response.json())
}

export async function deleteAdminCoupon(id: string) {
  const response = await fetch(`/api/v1/admin/coupons/${encodeURIComponent(id)}`, {
    credentials: "same-origin",
    method: "DELETE",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not delete coupon."))
  }
}

export async function loadAdminRewards(
  context: AdminRequestContext = {},
): Promise<AdminRewardCatalog> {
  return getAdminJson(
    "/api/v1/admin/rewards",
    adminRewardCatalogSchema,
    "Could not load rewards.",
    context,
  )
}

export async function createAdminReward(reward: AdminCreateRewardRequest): Promise<AdminReward> {
  const response = await fetch("/api/v1/admin/rewards", {
    body: JSON.stringify(reward),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not create reward."))
  }
  return adminRewardSchema.parse(await response.json())
}

export async function deleteAdminReward(id: string) {
  const response = await fetch(`/api/v1/admin/rewards/${encodeURIComponent(id)}`, {
    credentials: "same-origin",
    method: "DELETE",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not delete reward."))
  }
}

export async function loadAdminSettings(
  context: AdminRequestContext = {},
): Promise<AdminBrandingSettings> {
  return getAdminJson(
    "/api/v1/admin/settings",
    adminBrandingSettingsSchema,
    "Could not load organization settings.",
    context,
  )
}

/**
 * The branding PUT body. The contract exports the request as it parses, where a cleared legal link
 * reads "", so `privacyUrl` and `termsUrl` take the wire value null here, which removes the saved
 * link (R47). Left out, the saved privacy link stays.
 */
export type AdminBrandingSave = Omit<AdminUpdateBrandingRequest, "privacyUrl" | "termsUrl"> & {
  privacyUrl?: string | null
  termsUrl: string | null
}

export async function updateAdminBranding(
  settings: AdminBrandingSave,
): Promise<AdminBrandingSettings> {
  const response = await fetch("/api/v1/admin/settings/branding", {
    body: JSON.stringify(settings),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "PUT",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not update branding."))
  }
  return adminBrandingSettingsSchema.parse(await response.json())
}

/**
 * The advisory legal link check (G5): whether one HEAD request to `url` answered, and when it did
 * not, why (`reason`). The route exists only while the standard terms are available; any refusal
 * throws.
 */
export async function checkLegalLink(url: string): Promise<AdminLegalLinkCheck> {
  const response = await fetch("/api/v1/admin/legal/check-link", {
    body: JSON.stringify({ url }),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "Could not check the link."),
      response.status,
      responseRequestId(response),
    )
  }
  return adminLegalLinkCheckSchema.parse(await response.json())
}

export async function updateAdminWelcomeEmail(
  settings: AdminUpdateWelcomeEmailRequest,
): Promise<AdminBrandingSettings> {
  const response = await fetch("/api/v1/admin/settings/email", {
    body: JSON.stringify(settings),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "PUT",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not update the welcome email."))
  }
  return adminBrandingSettingsSchema.parse(await response.json())
}

export async function uploadAdminBrandingAsset(
  kind: "background" | "logo" | "bot-avatar" | "bot-banner",
  file: File,
): Promise<AdminBrandingAsset> {
  // The bot's own images (bot profile) go beside the branding ones, through their own route.
  const path =
    kind === "bot-avatar" || kind === "bot-banner"
      ? `/api/v1/admin/discord/bot-assets/${kind}`
      : `/api/v1/admin/settings/assets/${kind}`
  const response = await fetch(path, {
    body: file,
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": file.type },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not upload the branding image."))
  }
  return adminBrandingAssetSchema.parse(await response.json())
}

export async function loadAdminTeam(context: AdminRequestContext = {}): Promise<AdminTeamCatalog> {
  return getAdminJson(
    "/api/v1/admin/settings/team",
    adminTeamCatalogSchema,
    "Could not load the team.",
    context,
  )
}

export async function createAdminTeamMember(
  member: AdminCreateTeamMemberRequest,
): Promise<AdminTeamCatalog> {
  const response = await fetch("/api/v1/admin/settings/team", {
    body: JSON.stringify(member),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not add the teammate."))
  }
  return adminTeamCatalogSchema.parse(await response.json())
}

export async function inviteAdminTeamMembers(ids: string[]): Promise<AdminTeamCatalog> {
  const response = await fetch("/api/v1/admin/settings/team/invite", {
    body: JSON.stringify({ ids }),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not send the invitation."))
  }
  return adminTeamCatalogSchema.parse(await response.json())
}

export async function deleteAdminTeamMembers(ids: string[]): Promise<AdminTeamCatalog> {
  const response = await fetch("/api/v1/admin/settings/team", {
    body: JSON.stringify({ ids }),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "DELETE",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not remove the teammate."))
  }
  return adminTeamCatalogSchema.parse(await response.json())
}

export async function loadAdminAccount(
  context: AdminRequestContext = {},
): Promise<AdminAccountCatalog> {
  return getAdminJson(
    "/api/v1/admin/account",
    adminAccountCatalogSchema,
    "Could not load account billing.",
    context,
  )
}

export async function createAdminAccountSetupIntent(): Promise<AdminAccountSetupIntent> {
  const response = await fetch("/api/v1/admin/account/setup-intent", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not start secure card setup."))
  }
  return adminAccountSetupIntentSchema.parse(await response.json())
}

/** P3: the SetupIntent for the card an upgrade from Basic is paid with. */
export async function createAdminAccountUpgradeSetupIntent(): Promise<AdminAccountSetupIntent> {
  const response = await fetch("/api/v1/admin/account/upgrade/setup-intent", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not start secure card setup."))
  }
  return adminAccountSetupIntentSchema.parse(await response.json())
}

/**
 * P3: Basic moves to the chosen paid plan, paid by the card its SetupIntent saved. P4: a 202 is
 * the bank's check (3D Secure) first, and the same request is sent again after it.
 */
export async function upgradeAdminAccount(
  upgrade: AdminAccountUpgradeRequest,
): Promise<AdminAccountCatalog | AdminAccountUpgradeAction> {
  const response = await fetch("/api/v1/admin/account/upgrade", {
    body: JSON.stringify(upgrade),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not upgrade the plan."))
  }
  const body: unknown = await response.json()
  return response.status === 202
    ? adminAccountUpgradeActionSchema.parse(body)
    : adminAccountCatalogSchema.parse(body)
}

/** P4: the confirm read after the bank's check on a registration's first payment. */
export async function confirmAdminPayment(
  card: { paymentMethodId?: string } = {},
): Promise<AdminAccountPayment> {
  const response = await fetch("/api/v1/admin/account/confirm-payment", {
    // P4-F1: a new card for a failed check, saved through a SetupIntent on the same customer.
    ...(card.paymentMethodId ? { body: JSON.stringify(card) } : {}),
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(card.paymentMethodId ? { "Content-Type": "application/json" } : {}),
    },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not confirm the payment."))
  }
  return adminAccountPaymentSchema.parse(await response.json())
}

/** P4-F1: a first payment that can no longer be finished starts the plan again. */
/** P3-M2: Stripe refused the sign-up discount; the owner may start the plan at the full price. */
export class AdminDiscountRefusedError extends Error {}

export async function restartAdminPayment(
  options: { withoutDiscount?: boolean } = {},
): Promise<AdminAccountPayment> {
  const response = await fetch("/api/v1/admin/account/restart-payment", {
    ...(options.withoutDiscount ? { body: JSON.stringify({ withoutDiscount: true }) } : {}),
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(options.withoutDiscount ? { "Content-Type": "application/json" } : {}),
    },
    method: "POST",
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: unknown
      withoutDiscount?: unknown
    } | null
    const message = typeof body?.error === "string" ? body.error : "Could not start the plan again."
    throw body?.withoutDiscount === true
      ? new AdminDiscountRefusedError(message)
      : new Error(message)
  }
  return adminAccountPaymentSchema.parse(await response.json())
}

/**
 * P4-F1, L2: the confirm read, asked again up to 3 times 2 seconds apart while the bank's answer
 * is on its way (a payment Stripe.js just finished can take a moment to show as paid).
 */
export async function confirmAdminPaymentSettled(
  wait: (ms: number) => Promise<void> = (ms) => new Promise((done) => setTimeout(done, ms)),
): Promise<AdminAccountPayment> {
  let status = await confirmAdminPayment()
  for (let attempt = 1; attempt < 3 && !status.active && !status.restartable; attempt += 1) {
    await wait(2000)
    status = await confirmAdminPayment()
  }
  return status
}

export async function completeAdminAccountCard(
  paymentMethodId: string,
): Promise<AdminAccountCatalog> {
  const response = await fetch("/api/v1/admin/account/cards", {
    body: JSON.stringify({ paymentMethodId }),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not add the payment method."))
  }
  return adminAccountCatalogSchema.parse(await response.json())
}

export async function renewAdminAccountSubscription(
  paymentMethodId: string,
  idempotencyKey: string,
): Promise<AdminAccountCatalog> {
  const response = await fetch("/api/v1/admin/account/renew", {
    body: JSON.stringify({ paymentMethodId }),
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    },
    method: "POST",
  })
  if (!response.ok) {
    throw new AdminRenewalRequestError(
      await errorMessage(response, "Could not renew the subscription."),
      response.status,
    )
  }
  return adminAccountCatalogSchema.parse(await response.json())
}

export async function setDefaultAdminAccountCard(
  paymentMethodId: string,
): Promise<AdminAccountCatalog> {
  const response = await fetch("/api/v1/admin/account/cards/default", {
    body: JSON.stringify({ paymentMethodId }),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not update the default payment method."))
  }
  return adminAccountCatalogSchema.parse(await response.json())
}

export async function deleteAdminAccountCard(paymentMethodId: string): Promise<void> {
  const response = await fetch(
    `/api/v1/admin/account/cards/${encodeURIComponent(paymentMethodId)}`,
    {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      method: "DELETE",
    },
  )
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not remove the payment method."))
  }
}

export async function loadAdminDeveloperSettings(
  context: AdminRequestContext = {},
): Promise<AdminDeveloperSettings> {
  return getAdminJson(
    "/api/v1/admin/settings/developer",
    adminDeveloperSettingsSchema,
    "Could not load developer settings.",
    context,
  )
}

export async function rotateAdminDeveloperKey(): Promise<AdminDeveloperKeyIssued> {
  const response = await fetch("/api/v1/admin/settings/developer/key", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not rotate the API key."))
  }
  return adminDeveloperKeyIssuedSchema.parse(await response.json())
}

export async function loadAdminDomains(
  context: AdminRequestContext = {},
): Promise<AdminDomainCatalog> {
  return getAdminJson(
    "/api/v1/admin/domains",
    adminDomainCatalogSchema,
    "Could not load domains.",
    context,
  )
}

/** A pending claim of `hostname`; `primary` makes it the business's address once it is live. */
export async function addAdminDomain(input: {
  hostname: string
  primary: boolean
}): Promise<AdminDomainCatalog> {
  const response = await fetch("/api/v1/admin/domains", {
    body: JSON.stringify(input),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not add the domain."))
  }
  return adminDomainCatalogSchema.parse(await response.json())
}

export async function deleteAdminDomain(hostname: string): Promise<void> {
  const response = await fetch(`/api/v1/admin/domains/${encodeURIComponent(hostname)}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    method: "DELETE",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not delete the domain."))
  }
}

async function updateAdminDomainAction(
  hostname: string,
  action: "default" | "verify",
): Promise<AdminDomainCatalog> {
  const response = await fetch(`/api/v1/admin/domains/${encodeURIComponent(hostname)}/${action}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, `Could not ${action} the domain.`))
  }
  return adminDomainCatalogSchema.parse(await response.json())
}

export function setDefaultAdminDomain(hostname: string) {
  return updateAdminDomainAction(hostname, "default")
}

export function verifyAdminDomain(hostname: string) {
  return updateAdminDomainAction(hostname, "verify")
}

/** The business's automatic address, or null when the platform gives it none (no base domain). */
export async function loadAdminAddress(
  context: AdminRequestContext = {},
): Promise<AdminAddress | null> {
  return getAdminJson(
    "/api/v1/admin/address",
    adminAddressSchema.nullable(),
    "Could not load the address.",
    context,
  )
}

export async function checkAdminAddress(
  handle: string,
  context: AdminRequestContext = {},
): Promise<AdminAddressCheck> {
  return getAdminJson(
    `/api/v1/admin/address/check?handle=${encodeURIComponent(handle)}`,
    adminAddressCheckSchema,
    "Could not check the address.",
    context,
  )
}

export async function saveAdminAddress(handle: string): Promise<AdminAddress> {
  const response = await fetch("/api/v1/admin/address", {
    body: JSON.stringify({ handle }),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "PUT",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not change the address."))
  }
  return adminAddressSchema.parse(await response.json())
}

export async function loadAdminTrackingPixels(
  context: AdminRequestContext = {},
): Promise<AdminTrackingPixels> {
  return getAdminJson(
    "/api/v1/admin/pixels",
    adminTrackingPixelsSchema,
    "Could not load tracking pixels.",
    context,
  )
}

export async function updateAdminTrackingPixels(
  pixels: AdminUpdateTrackingPixelsRequest,
): Promise<AdminTrackingPixels> {
  const response = await fetch("/api/v1/admin/pixels", {
    body: JSON.stringify(pixels),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "PUT",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not update tracking pixels."))
  }
  return adminTrackingPixelsSchema.parse(await response.json())
}

export async function loadAdminDiscord(
  context: AdminRequestContext = {},
): Promise<AdminDiscordConnection> {
  return getAdminJson(
    "/api/v1/admin/discord",
    adminDiscordConnectionSchema,
    "Could not load Discord settings.",
    context,
  )
}

async function updateAdminDiscord(
  path: "bot" | "embeds" | "webhooks",
  body:
    | AdminUpdateDiscordBotRequest
    | AdminUpdateDiscordEmbedsRequest
    | AdminUpdateDiscordWebhooksRequest,
): Promise<AdminDiscordConnection> {
  const response = await fetch(`/api/v1/admin/discord/${path}`, {
    body: JSON.stringify(body),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "PUT",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not update Discord settings."))
  }
  return adminDiscordConnectionSchema.parse(await response.json())
}

export function updateAdminDiscordWebhooks(body: AdminUpdateDiscordWebhooksRequest) {
  return updateAdminDiscord("webhooks", body)
}

export function updateAdminDiscordBot(body: AdminUpdateDiscordBotRequest) {
  return updateAdminDiscord("bot", body)
}

export function updateAdminDiscordEmbeds(body: AdminUpdateDiscordEmbedsRequest) {
  return updateAdminDiscord("embeds", body)
}

/**
 * Create role (G4): a role with no permissions, made by the bot in one of the business's servers.
 * Discord puts it at the bottom, below the bot, so the bot can give it. A refusal is an
 * AdminApiError with the API's words, such as a bot without Manage Roles (409).
 */
export async function createDiscordRole(
  guildId: string,
  name: string,
): Promise<AdminProductGuild["roles"][number]> {
  const response = await fetch(
    `/api/v1/admin/discord/guilds/${encodeURIComponent(guildId)}/roles`,
    {
      body: JSON.stringify({ name } satisfies AdminCreateDiscordRoleRequest),
      credentials: "same-origin",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      method: "POST",
    },
  )
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "The role could not be created. Try again."),
      response.status,
      responseRequestId(response),
    )
  }
  return adminDiscordRoleSchema.parse(await response.json())
}

export async function setAdminDiscordConnection(connected: boolean) {
  const response = await fetch(`/api/v1/admin/discord/${connected ? "connect" : "disconnect"}`, {
    credentials: "same-origin",
    method: "POST",
  })
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Could not change the Discord connection."))
  }
  return adminDiscordConnectionSchema.parse(await response.json())
}

const MEMBER_TRACKING = "/api/v1/admin/community/members"

/** Member tracking: whether the business tracks who is in its server, and when it last checked. */
export async function getMemberTracking(
  context: AdminRequestContext = {},
): Promise<AdminMemberTracking> {
  return getAdminJson(
    MEMBER_TRACKING,
    adminMemberTrackingSchema,
    "Could not load member tracking.",
    context,
  )
}

/** Turns "Track who is in your server" on or off; owners and admins only. */
export async function setMemberTracking(tracking: boolean): Promise<AdminMemberTracking> {
  const response = await fetch(MEMBER_TRACKING, {
    body: JSON.stringify({ tracking } satisfies AdminMemberTrackingRequest),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "PUT",
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "Member tracking could not be changed. Try again."),
      response.status,
      responseRequestId(response),
    )
  }
  return adminMemberTrackingSchema.parse(await response.json())
}

/** Asks for every server's members to be checked now; the worker does it within a minute. */
export async function checkMembersNow(): Promise<AdminMemberTracking> {
  const response = await fetch(`${MEMBER_TRACKING}/check`, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "Members could not be checked. Try again."),
      response.status,
      responseRequestId(response),
    )
  }
  return adminMemberTrackingSchema.parse(await response.json())
}

const botProfilePath = (guildId: string) =>
  `/api/v1/admin/discord/guilds/${encodeURIComponent(guildId)}/bot-profile`

/** The bot's profile in one server: the draft, when it was applied, and what Discord shows now. */
export async function getBotProfile(
  guildId: string,
  context: AdminRequestContext = {},
): Promise<AdminBotProfile> {
  return getAdminJson(
    botProfilePath(guildId),
    adminBotProfileSchema,
    "Could not load the bot's profile.",
    context,
  )
}

/** Saves the bot's profile draft for a server; nothing changes in Discord until it is applied. */
export async function saveBotProfile(guildId: string, draft: AdminBotProfileRequest) {
  const response = await fetch(botProfilePath(guildId), {
    body: JSON.stringify(draft satisfies AdminBotProfileRequest),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "PUT",
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "The bot's profile could not be saved. Try again."),
      response.status,
      responseRequestId(response),
    )
  }
}

/** Sends the saved draft to Discord (`apply`), or Discord's defaults (`reset`). */
export async function sendBotProfile(
  guildId: string,
  action: "apply" | "reset",
): Promise<AdminBotProfileApply> {
  const response = await fetch(`${botProfilePath(guildId)}/${action}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "The bot's profile could not be changed. Try again."),
      response.status,
      responseRequestId(response),
    )
  }
  return adminBotProfileApplySchema.parse(await response.json())
}

/** One page of the CRM list (§3.5), `cursor` being the previous page's `nextCursor`. */
export async function loadAdminCustomers(
  filters: CustomerFilters,
  cursor: string | null,
  context: AdminRequestContext = {},
): Promise<AdminCustomerList> {
  return getAdminJson(
    `/api/v1/admin/customers?${customerQuery(filters, cursor)}`,
    adminCustomerListSchema,
    "Could not load customers.",
    context,
  )
}

/**
 * One customer's record and journey (§3.5). An id that is not an ObjectId, such as "facets" or
 * "export.csv" from a typed URL, is a 404 without a request: those paths are other endpoints.
 */
export async function loadAdminCustomer(
  id: string,
  context: AdminRequestContext = {},
): Promise<AdminCustomerDetail> {
  if (!objectIdSchema.safeParse(id).success) {
    throw new AdminApiError("Customer not found.", 404, null)
  }
  return getAdminJson(
    `/api/v1/admin/customers/${id}`,
    adminCustomerDetailSchema,
    "Could not load the customer.",
    context,
  )
}

/** The values the list's filters offer: the channels, sources, campaigns and products in use. */
export async function loadAdminCustomerFacets(
  context: AdminRequestContext = {},
): Promise<AdminCustomerFacets> {
  return getAdminJson(
    "/api/v1/admin/customers/facets",
    adminCustomerFacetsSchema,
    "Could not load the customer filters.",
    context,
  )
}

/**
 * The dashboard's counts for `range` (§3.5), with "today" in the browser's time zone. A zone the
 * browser's own Intl refuses, such as "Etc/Unknown" on a misconfigured machine, would be a 400, so
 * it falls back to UTC, the API's default.
 */
export async function loadAdminDashboardSummary(
  range: AdminDashboardRange,
  context: AdminRequestContext = {},
): Promise<AdminDashboardSummary> {
  const zone = adminDashboardQuerySchema.safeParse({
    range,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  })
  const query = new URLSearchParams({ range, tz: zone.success ? zone.data.tz : "UTC" })
  return getAdminJson(
    `/api/v1/admin/dashboard/summary?${query}`,
    adminDashboardSummarySchema,
    "Could not load the dashboard.",
    context,
  )
}

/** What setup is left (G7), read from the business's own data, for the dashboard's card. */
export async function loadAdminSetup(context: AdminRequestContext = {}): Promise<AdminSetup> {
  return getAdminJson("/api/v1/admin/setup", adminSetupSchema, "Could not load setup.", context)
}

/**
 * A fresh onboarding link for the signed-in owner (G7), which retires the one before it. A
 * refusal (409 finished, 409 owner only, 403 read-only or member) is an AdminApiError with the
 * API's own words.
 */
export async function resumeOnboarding(): Promise<AdminOnboardingLink> {
  const response = await fetch(adminOnboardingPath("link"), {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "Could not reopen setup."),
      response.status,
      responseRequestId(response),
    )
  }
  return adminOnboardingLinkSchema.parse(await response.json())
}

/** The name in a `Content-Disposition: attachment; filename="…"` header. */
function attachmentName(response: Response) {
  return /filename="([^"]+)"/.exec(response.headers.get("Content-Disposition") ?? "")?.[1]
}

const EXPORT_TYPES = {
  csv: "text/csv",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
} as const

/**
 * Downloads every customer the filters match as a CSV, or an Excel workbook where the deployment
 * offers member tracking (owners and admins only; the API audits it). The file is fetched first,
 * so a refusal is an AdminApiError the page can show instead of a file holding the error. The
 * API's paging and sort do not apply: the file is newest first.
 */
export async function downloadAdminCustomers(
  filters: CustomerFilters,
  format: keyof typeof EXPORT_TYPES = "csv",
): Promise<void> {
  const query = customerQuery(filters, null)
  for (const key of ["dir", "limit", "sort"]) query.delete(key)
  const response = await fetch(`/api/v1/admin/customers/export.${format}?${query}`, {
    credentials: "same-origin",
    headers: { Accept: EXPORT_TYPES[format] },
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "Could not export customers."),
      response.status,
      responseRequestId(response),
    )
  }
  // ponytail: the whole file is held as a Blob before it saves, about 50 MB at 158K customers.
  // Stream it to disk with the File System Access API if exports grow past what a tab holds.
  await saveDownload(response, `customers-${new Date().toISOString().slice(0, 10)}.${format}`)
}

// Saves a fetched file under the name the API gave it, or `fallback`.
async function saveDownload(response: Response, fallback: string) {
  const link = document.createElement("a")
  link.href = URL.createObjectURL(await response.blob())
  link.download = attachmentName(response) ?? fallback
  link.click()
  // Later, not at once: Safari and some Firefox builds cancel a save whose address is revoked.
  setTimeout(() => URL.revokeObjectURL(link.href), 1_000)
}

const POLLS = "/api/v1/admin/discord/polls"

// A refusal as an AdminApiError with the API's words, or `fallback`.
async function refused(response: Response, fallback: string): Promise<never> {
  throw new AdminApiError(
    await errorMessage(response, fallback),
    response.status,
    responseRequestId(response),
  )
}

/** One page of the business's polls, newest first, `cursor` being the previous page's `nextCursor`. */
export async function loadPolls(
  cursor: string | null,
  context: AdminRequestContext = {},
): Promise<AdminPollList> {
  return getAdminJson(
    cursor ? `${POLLS}?cursor=${encodeURIComponent(cursor)}` : POLLS,
    adminPollListSchema,
    "Could not load polls.",
    context,
  )
}

/** A server's text channels, each with what the bot lacks to post polls there. */
export async function loadPollChannels(
  guildId: string,
  context: AdminRequestContext = {},
): Promise<AdminPollChannels> {
  return getAdminJson(
    `/api/v1/admin/discord/guilds/${encodeURIComponent(guildId)}/poll-channels`,
    adminPollChannelsSchema,
    "Could not load the server's channels.",
    context,
  )
}

/** Posts a native poll as the Ternary bot; owners and admins only. */
export async function postPoll(poll: AdminPollCreateRequest): Promise<AdminPoll> {
  const response = await fetch(POLLS, {
    body: JSON.stringify(poll satisfies AdminPollCreateRequest),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) await refused(response, "The poll could not be posted. Try again.")
  return adminPollSchema.parse(await response.json())
}

/** Ends a poll now (`end`), or reads its votes again (`refresh`). */
export async function changePoll(id: string, action: "end" | "refresh"): Promise<AdminPoll> {
  const response = await fetch(`${POLLS}/${encodeURIComponent(id)}/${action}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    await refused(
      response,
      action === "end"
        ? "The poll could not be ended. Try again."
        : "Votes could not be read. Try again.",
    )
  }
  return adminPollSchema.parse(await response.json())
}

/** Downloads one row per voter of a poll, as CSV or an Excel workbook (owners and admins). */
export async function downloadPollVotes(
  id: string,
  format: keyof typeof EXPORT_TYPES,
): Promise<void> {
  const response = await fetch(`${POLLS}/${encodeURIComponent(id)}/export.${format}`, {
    credentials: "same-origin",
    headers: { Accept: EXPORT_TYPES[format] },
  })
  if (!response.ok) await refused(response, "Could not export the poll's votes.")
  await saveDownload(response, `poll-${id}.${format}`)
}

// Saved posts (COMMUNITY_FEATURES saved_posts).
const COMMUNITY = "/api/v1/admin/community"

/** The saved channels, the channels to choose from, and whether Discord gives message text. */
export async function loadSavedChannels(
  context: AdminRequestContext = {},
): Promise<AdminSavedChannels> {
  return getAdminJson(
    `${COMMUNITY}/saved-channels`,
    adminSavedChannelsSchema,
    "Could not load the saved channels.",
    context,
  )
}

/** Saves the whole list of saved channels; owners and admins only. */
export async function saveSavedChannels(channels: AdminSavedChannelsRequest["channels"]) {
  const response = await fetch(`${COMMUNITY}/saved-channels`, {
    body: JSON.stringify({ channels } satisfies AdminSavedChannelsRequest),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "PUT",
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "The saved channels could not be changed. Try again."),
      response.status,
      responseRequestId(response),
    )
  }
}

/** The saved posts list's filters. */
export interface SavedPostFilters {
  channelId?: string | undefined
  /** Days as YYYY-MM-DD, both included. */
  from?: string | undefined
  to?: string | undefined
  search?: string | undefined
  sort: "time" | "channel"
}

/** One page of saved posts, `cursor` being the previous page's `nextCursor`. */
export async function loadSavedPosts(
  filters: SavedPostFilters,
  cursor: string | null,
  context: AdminRequestContext = {},
): Promise<AdminSavedPostList> {
  const query = new URLSearchParams({ sort: filters.sort })
  if (filters.channelId) query.set("channelId", filters.channelId)
  if (filters.from) query.set("from", filters.from)
  if (filters.to) query.set("to", filters.to)
  if (filters.search) query.set("search", filters.search)
  if (cursor) query.set("cursor", cursor)
  return getAdminJson(
    `${COMMUNITY}/saved-posts?${query}`,
    adminSavedPostListSchema,
    "Could not load saved posts.",
    context,
  )
}

/** A saved post with its thread replies. */
export async function loadSavedPost(
  id: string,
  context: AdminRequestContext = {},
): Promise<AdminSavedPostDetail> {
  return getAdminJson(
    `${COMMUNITY}/saved-posts/${encodeURIComponent(id)}`,
    adminSavedPostDetailSchema,
    "Could not load the post.",
    context,
  )
}

// Settings → Developer → Claude (MCP), and the consent page Claude's sign-in opens.
const MCP_SETTINGS = "/api/v1/admin/settings/mcp"
const MCP_REQUESTS = "/api/v1/admin/oauth/requests"

/** The MCP URL, whether this person may connect, and their personal tokens. */
export async function loadMcpTokens(context: AdminRequestContext = {}): Promise<AdminMcpTokenList> {
  return getAdminJson(
    `${MCP_SETTINGS}/tokens`,
    adminMcpTokenListSchema,
    "Could not load your Claude tokens.",
    context,
  )
}

/** A new personal token: its value comes back once. */
export async function createMcpToken(label: string): Promise<AdminMcpTokenCreated> {
  const response = await fetch(`${MCP_SETTINGS}/tokens`, {
    body: JSON.stringify({ label }),
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "The token could not be created. Try again."),
      response.status,
      responseRequestId(response),
    )
  }
  return adminMcpTokenCreatedSchema.parse(await response.json())
}

async function deleteMcp(path: string, fallback: string) {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    method: "DELETE",
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, fallback),
      response.status,
      responseRequestId(response),
    )
  }
}

export function revokeMcpToken(id: string) {
  return deleteMcp(
    `${MCP_SETTINGS}/tokens/${encodeURIComponent(id)}`,
    "The token could not be revoked. Try again.",
  )
}

/** The apps this person connected through Claude's sign-in. */
export async function loadMcpConnections(
  context: AdminRequestContext = {},
): Promise<AdminMcpConnectionList> {
  return getAdminJson(
    `${MCP_SETTINGS}/connections`,
    adminMcpConnectionListSchema,
    "Could not load your connected apps.",
    context,
  )
}

export function revokeMcpConnection(id: string) {
  return deleteMcp(
    `${MCP_SETTINGS}/connections/${encodeURIComponent(id)}`,
    "The app could not be disconnected. Try again.",
  )
}

/** For owners: every other person's live MCP tokens and connected apps in the business. */
export async function loadMcpTeam(context: AdminRequestContext = {}): Promise<AdminMcpTeam> {
  return getAdminJson(
    `${MCP_SETTINGS}/team`,
    adminMcpTeamSchema,
    "Could not load your team's Claude access.",
    context,
  )
}

/** For owners: ends one teammate's personal token or connected app. */
export function revokeMcpTeammateAccess(access: {
  adminId: string
  id: string
  kind: "token" | "connection"
}) {
  const kind = access.kind === "token" ? "tokens" : "connections"
  return deleteMcp(
    `${MCP_SETTINGS}/team/${encodeURIComponent(access.adminId)}/${kind}/${encodeURIComponent(access.id)}`,
    "That access could not be ended. Try again.",
  )
}

/** A pending Claude sign-in, for the consent page. */
export async function loadMcpRequest(
  id: string,
  context: AdminRequestContext = {},
): Promise<AdminMcpOauthRequest> {
  return getAdminJson(
    `${MCP_REQUESTS}/${encodeURIComponent(id)}`,
    adminMcpOauthRequestSchema,
    "Could not load this sign-in.",
    context,
  )
}

/** Allow or Cancel: where the browser goes next, the client's own redirect address. */
export async function decideMcpRequest(id: string, decision: "approve" | "deny") {
  const response = await fetch(`${MCP_REQUESTS}/${encodeURIComponent(id)}/${decision}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    method: "POST",
  })
  if (!response.ok) {
    throw new AdminApiError(
      await errorMessage(response, "Claude could not be connected. Try again."),
      response.status,
      responseRequestId(response),
    )
  }
  return adminMcpOauthDecisionSchema.parse(await response.json()).redirectTo
}
